'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

interface ServerData {
  id: string;
  name: string;
  description: string;
  mapSize: string;
  wipeSchedule: string;
  maxPlayers: number;
  currentPlayers: number;
  teamLimit: string;
  tag: string;
  tagColor: string;
  ip: string;
  imageUrl: string;
  imageAlt: string;
  features: string[];
  comingSoon?: boolean;
}

const SERVERS: ServerData[] = [
{
  id: 'eu-main',
  name: 'EU Main',
  description: 'The real VANTA experience. Full-size map, balanced gameplay, no blueprint wipes, QoL improvments as 50% Up-Keep',
  mapSize: '4000',
  wipeSchedule: 'On Force',
  maxPlayers: 150,
  currentPlayers: 0, // Will be updated from API
  teamLimit: '8',
  tag: 'FLAGSHIP',
  tagColor: '#6a5cff',
  ip: 'main.vanta.gg',
  imageUrl: "/assets/images/image-1771565654745.png",
  imageAlt: 'Dense forest landscape at dusk representing EU Main server terrain',
  features: ['4500 Map', 'Monthly BP Wipe', 'Active Admins', '150 Slots']
},
{
  id: 'eu-trio',
  name: 'EU Trio',
  description: 'Balanced 3-man team cap. Tight squad play, strategic base building, competitive wipes.',
  mapSize: '3500',
  wipeSchedule: 'Thursdays',
  maxPlayers: 150,
  currentPlayers: 112,
  teamLimit: 'Trio (3)',
  tag: 'TRIO',
  tagColor: '#f59e0b',
  ip: 'trio.vanta.gg',
  imageUrl: "/assets/images/image-1771567593507.png",
  imageAlt: 'Three armed Rust players overlooking dramatic sunset scene with mountains and river',
  features: ['3500 Map', 'Monthly BP Wipe', 'Team Cap: 3', '150 Slots'],
  comingSoon: true
},
{
  id: 'eu-solo',
  name: 'EU Solo',
  description: 'Prove yourself alone. Solo-only server with a smaller map and weekly wipes for fast-paced action.',
  mapSize: '2500',
  wipeSchedule: 'Fridays',
  maxPlayers: 100,
  currentPlayers: 67,
  teamLimit: 'Solo (1)',
  tag: 'SOLO',
  tagColor: '#22c55e',
  ip: 'solo.vanta.gg',
  imageUrl: "/assets/images/image-1771565766975.png",
  imageAlt: 'Solo player in dramatic sunset landscape representing the EU Solo server experience',
  features: ['2500 Map', 'Weekly BP Wipe', 'Solo Only', '100 Slots'],
  comingSoon: true
}];


function ServerCard({ server, index, isLoadingStatus }: {server: ServerData;index: number;isLoadingStatus: boolean;}) {
  const fillPercent = Math.round((server?.currentPlayers || 0) / (server?.maxPlayers || 1) * 100);
  const fillColor = fillPercent > 80 ? '#ef4444' : fillPercent > 50 ? '#f59e0b' : '#22c55e';

  return (
    <div
      className="server-card-premium reveal-hidden group"
      style={{
        transitionDelay: `${index * 0.1}s`
      }}
      role="article"
      aria-label={`${server?.name || 'Server'} server`}>
      
      {/* Premium glow effect on hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${server?.tagColor || '#6a5cff'}15, transparent 70%)`,
          filter: 'blur(20px)'
        }}
      />
      
      {/* Image */}
      <div className="relative h-56 overflow-hidden rounded-t-2xl">
        <Image
          src={server?.imageUrl || '/assets/images/no_image.png'}
          alt={server?.imageAlt || 'Server image'}
          width={600}
          height={400}
          className="w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-90 group-hover:scale-110"
          loading="lazy"
          quality={75}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
        />
        
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#14141a]/50 to-[#14141a]" />
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500"
          style={{
            background: `linear-gradient(135deg, ${server?.tagColor || '#6a5cff'}20, transparent 60%)`
          }}
        />
        
        {/* Coming Soon Badge or Live count - Top Right */}
        {server?.comingSoon ? (
          <div className="absolute top-5 right-5">
            <div className="relative">
              <div 
                className="absolute inset-0 blur-2xl opacity-70"
                style={{
                  background: `radial-gradient(circle, ${server?.tagColor || '#6a5cff'}, transparent)`,
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              />
              <span
                className="relative block text-base font-900 px-5 py-2.5 rounded-lg backdrop-blur-sm"
                style={{
                  background: `${server?.tagColor || '#6a5cff'}30`,
                  color: server?.tagColor || '#6a5cff',
                  border: `2px solid ${server?.tagColor || '#6a5cff'}`,
                  fontWeight: 900,
                  textShadow: `0 0 25px ${server?.tagColor || '#6a5cff'}90`,
                  boxShadow: `0 0 35px ${server?.tagColor || '#6a5cff'}50, inset 0 0 25px ${server?.tagColor || '#6a5cff'}25`
                }}>
                Coming Soon!
              </span>
            </div>
          </div>
        ) : (
          <div className="absolute top-5 right-5 flex items-center gap-2 bg-black/70 backdrop-blur-xl px-4 py-2 rounded-xl border border-white/20 shadow-xl transition-all duration-300 group-hover:border-white/30 group-hover:bg-black/80">
            {isLoadingStatus ? (
              <span className="text-sm font-700 text-white/60" style={{ fontWeight: 700 }}>
                Checking server status...
              </span>
            ) : (
              <>
                <span className="live-dot" aria-hidden="true" />
                <span className="text-sm font-700 text-white" style={{ fontWeight: 700 }}>
                  {server?.currentPlayers || 0}/{server?.maxPlayers || 0}
                </span>
              </>
            )}
          </div>
        )}
        
        {/* Tag - Bottom Left Corner */}
        <div className="absolute bottom-5 left-5">
          <span
            className="tag-badge-premium text-sm font-800 px-4 py-2 rounded-tr-2xl rounded-bl-2xl backdrop-blur-md transition-all duration-300 group-hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${server?.tagColor || '#6a5cff'}30, ${server?.tagColor || '#6a5cff'}20)`,
              color: server?.tagColor || '#6a5cff',
              border: `2px solid ${server?.tagColor || '#6a5cff'}60`,
              fontWeight: 800,
              boxShadow: `0 4px 20px ${server?.tagColor || '#6a5cff'}50, inset 0 1px 0 ${server?.tagColor || '#6a5cff'}40`
            }}>
            {server?.tag || 'SERVER'}
          </span>
        </div>
      </div>

      {/* Content - Fixed height container */}
      <div className="p-7 flex flex-col" style={{ minHeight: '420px' }}>
        <h3 className="font-heading text-2xl font-800 text-white mb-3 transition-colors duration-300 group-hover:text-vanta-accent" style={{ fontWeight: 800 }}>
          {server?.name || 'Server'}
        </h3>
        <p className="text-sm text-vanta-muted leading-relaxed mb-6">
          {server?.description || 'Server description'}
        </p>

        {server?.comingSoon ? (
          // Coming Soon placeholder content to maintain consistent height
          <div className="flex-1 flex flex-col justify-between">
            <div className="space-y-4 mb-6">
              {/* Placeholder for capacity bar */}
              <div className="opacity-30">
                <div className="flex justify-between text-xs text-vanta-dim mb-2.5 font-semibold">
                  <span>Server Capacity</span>
                  <span>--</span>
                </div>
                <div className="server-fill-bar-hex">
                  <div className="server-fill-bar-inner-hex" style={{ width: '0%', background: '#6a5cff' }} />
                </div>
              </div>

              {/* Placeholder meta grid */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Map Size', value: `${server?.mapSize || '0'}` },
                  { label: 'Team Limit', value: server?.teamLimit || 'N/A' },
                  { label: 'Wipe Day', value: server?.wipeSchedule || 'N/A' },
                  { label: 'Slots', value: `${server?.maxPlayers || 0}` }
                ].map((item) => (
                  <div key={item.label} className="bg-[#0b0b0f]/50 rounded-xl px-4 py-3 border border-white/5 backdrop-blur-sm">
                    <div className="text-[11px] text-vanta-dim uppercase tracking-widest mb-1 font-semibold">{item.label}</div>
                    <div className="text-base font-700 text-vanta-text/50" style={{ fontWeight: 700 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coming Soon button */}
            <button
              disabled
              className="w-full py-4 text-base text-center flex items-center justify-center gap-2.5 rounded-xl font-bold transition-all duration-300 cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${server?.tagColor || '#6a5cff'}20, ${server?.tagColor || '#6a5cff'}10)`,
                border: `2px solid ${server?.tagColor || '#6a5cff'}40`,
                color: server?.tagColor || '#6a5cff',
                boxShadow: `0 4px 15px ${server?.tagColor || '#6a5cff'}20`
              }}
              aria-label={`${server?.name || 'Server'} coming soon`}>
              Coming Soon
            </button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-between">
            <div className="space-y-4 mb-6">
              {/* Server fill bar */}
              <div>
                <div className="flex justify-between text-xs text-vanta-dim mb-2.5 font-semibold">
                  <span>Server Capacity</span>
                  <span style={{ color: fillColor, fontWeight: 700 }}>{fillPercent}%</span>
                </div>
                <div className="server-fill-bar-hex" role="progressbar" aria-valuenow={fillPercent} aria-valuemin={0} aria-valuemax={100} aria-label={`Server ${fillPercent}% full`}>
                  <div
                    className="server-fill-bar-inner-hex"
                    style={{
                      width: `${fillPercent}%`,
                      background: `linear-gradient(90deg, ${fillColor}99, ${fillColor})`,
                      boxShadow: `0 0 10px ${fillColor}60`
                    }} />
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Map Size', value: `${server?.mapSize || '0'}` },
                  { label: 'Team Limit', value: server?.teamLimit || 'N/A' },
                  { label: 'Wipe Day', value: server?.wipeSchedule || 'N/A' },
                  { label: 'Slots', value: `${server?.maxPlayers || 0}` }
                ].map((item) => (
                  <div key={item.label} className="bg-[#0b0b0f] rounded-xl px-4 py-3 border border-white/5 backdrop-blur-sm transition-all duration-300 hover:border-white/10 hover:bg-[#0b0b0f]/80">
                    <div className="text-[11px] text-vanta-dim uppercase tracking-widest mb-1 font-semibold">{item.label}</div>
                    <div className="text-base font-700 text-vanta-text" style={{ fontWeight: 700 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Connect Button */}
            <a
              href={`steam://connect/${server?.ip || ''}`}
              className="btn-primary-premium w-full py-4 text-base text-center flex items-center justify-center gap-2.5 group/btn"
              aria-label={`Connect to ${server?.name || 'server'}`}>
              Connect
              <svg className="w-5 h-5 transition-transform group-hover/btn:translate-x-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServerSelection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [serverData, setServerData] = useState<ServerData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Changed to false since we're not fetching
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // TEMPORARILY DISABLED: Live server status fetching
  // Uncomment when server-side configuration is ready
  /*
  // Fetch live player count from API
  const fetchServerStatus = async () => {
    try {
      const response = await fetch('/api/server-status');
      
      if (!response?.ok) {
        console.error('Server status fetch failed: HTTP', response?.status);
        setServerData(SERVERS);
        setIsLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data?.online) {
        setServerData((prev) => {
          const base = prev || SERVERS;
          return base.map((server) => {
            // Update EU Main server with live data
            if (server?.id === 'eu-main') {
              return {
                ...server,
                currentPlayers: data?.players || 0,
                maxPlayers: data?.maxPlayers || server?.maxPlayers || 150
              };
            }
            return server;
          });
        });
        setLastUpdate(new Date());
      } else {
        // Server offline - use default data
        setServerData(SERVERS);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch server status:', error);
      setServerData(SERVERS);
      setIsLoading(false);
    }
  };

  // Initial fetch and auto-refresh every 45 seconds
  useEffect(() => {
    fetchServerStatus();
    const interval = setInterval(fetchServerStatus, 45000);
    return () => clearInterval(interval);
  }, []);
  */

  // Use static data until live stats are configured
  useEffect(() => {
    setServerData(SERVERS);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry?.isIntersecting) {
            entry.target?.classList?.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.reveal-hidden');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [serverData]);

  // Don't render until data exists
  if (!serverData) {
    return (
      <section
        id="servers"
        className="relative py-32 px-6"
        aria-labelledby="servers-heading">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <p className="text-vanta-muted text-lg">Checking server status...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="servers"
      ref={sectionRef}
      className="relative py-32 px-6"
      aria-labelledby="servers-heading">
      
      {/* Section glow */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(106,92,255,0.06) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }} />
      

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <div className="section-label mb-4">Choose Your Battlefield</div>
          <h2
            id="servers-heading"
            className="font-heading text-4xl md:text-5xl font-800 text-white mb-4"
            style={{ fontWeight: 800 }}>
            
            Select Your Server
          </h2>
          <p className="text-vanta-muted max-w-xl mx-auto leading-relaxed">
            Three EU vanilla servers, each crafted for a different playstyle.
            All running on dedicated hardware in Frankfurt.
          </p>
        </div>

        {/* Server Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serverData?.map((server, i) =>
          <ServerCard key={server?.id || i} server={server} index={i} isLoadingStatus={isLoading} />
          )}
        </div>

        {/* Bottom note */}
        <div className="text-center mt-10 reveal-hidden">
          <p className="text-xs text-vanta-dim uppercase tracking-widest">
            All servers hosted on dedicated hardware · Frankfurt, DE · &lt;15ms avg ping
          </p>
        </div>
      </div>
    </section>);

}